import React from 'react';

export const HapticFeedbackPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-zinc-200">Haptic Feedback</h1>

      <p className="text-zinc-400 mb-8">
        Our haptic feedback system transforms your music listening experience by adding a tactile
        dimension to your favorite songs. Learn how to make the most of this innovative feature.
      </p>

      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-zinc-200">How It Works</h2>
          <div className="text-zinc-400 space-y-4">
            <p>
              The Haptic Music Player analyzes your music in real-time, converting audio patterns
              into precise haptic feedback signals. These signals are synchronized with:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Bass frequencies for deep vibrations</li>
              <li>Beat patterns for rhythmic feedback</li>
              <li>Musical transitions for dynamic responses</li>
              <li>Volume changes for intensity variation</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-zinc-200">Supported Devices</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-zinc-800/50 backdrop-blur p-4 rounded-lg border border-zinc-700/50">
              <h3 className="font-medium mb-2 text-zinc-200">Built-in Haptics</h3>
              <ul className="list-disc list-inside text-zinc-400">
                <li>Laptop touchpads</li>
                <li>Gaming mice</li>
                <li>Mobile devices</li>
              </ul>
            </div>
            <div className="bg-zinc-800/50 backdrop-blur p-4 rounded-lg border border-zinc-700/50">
              <h3 className="font-medium mb-2 text-zinc-200">External Devices</h3>
              <ul className="list-disc list-inside text-zinc-400">
                <li>Haptic wearables</li>
                <li>Gaming controllers</li>
                <li>USB haptic devices</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-zinc-200">Customization</h2>
          <div className="bg-zinc-800/50 backdrop-blur p-6 rounded-lg border border-zinc-700/50">
            <h3 className="font-medium mb-4 text-zinc-200">Intensity Levels</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <div className="text-yellow-500 font-semibold mb-2">Low</div>
                <p className="text-zinc-400 text-sm">
                  Subtle feedback for background listening
                </p>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <div className="text-yellow-500 font-semibold mb-2">Medium</div>
                <p className="text-zinc-400 text-sm">
                  Balanced feedback for general use
                </p>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <div className="text-yellow-500 font-semibold mb-2">High</div>
                <p className="text-zinc-400 text-sm">
                  Intense feedback for immersive experience
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-zinc-200">Tips for the Best Experience</h2>
          <ul className="list-disc list-inside space-y-2 text-zinc-400">
            <li>Start with lower intensity settings and gradually increase to your preference</li>
            <li>Experiment with different pattern presets for various music genres</li>
            <li>Ensure your device drivers are up to date</li>
            <li>Use high-quality audio files for the most accurate haptic response</li>
            <li>Consider your device's battery life when using haptic feedback</li>
          </ul>
        </section>
      </div>
    </div>
  );
};
